import { Button, Grid, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks/hooks';
import { getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { IconStateType, OpCo, PartnerId } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import ConfirmationHeader from '../ConfirmationHeader/ConfirmationHeader';
import ModalLinearActivityIndicator from '../ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import PropertyServiceInfo from '../PropertyServiceInfo/PropertyServiceInfo';
import SelectedServiceInfoSummary from '../SelectedServiceInfoSummary/SelectedServiceInfoSummary';
import StepProgressBar from '../StepProgressBar/StepProgressBar';

export default function FinalServicesConfirmation() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const availableProgressStages = useAppSelector(
    (state: { services: { availableProgressStages: any } }) => state.services.availableProgressStages
  );
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  const nextStepsText =
    'You will receive further communication on your claim as it progresses. Please take reasonable steps to secure your property and prevent further damage.';
  const reminderText = 'You can always return to the available property service(s) by viewing your claim’s details.';
  const headerTextWhenServicesSelected = 'Property Services Summary';
  const headerTextWhenServicesNotSelected = 'You have skipped all available Property Service(s)';

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }
  const selectedProviders = useAppSelector(
    (state: { services: { selectedServiceProviders: any } }) => state.services.selectedServiceProviders
  );
  const isServicesSelected = selectedProviders.length > 0 ? true : false;

  const contentWhenServicesSelected = (
    <>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader
            headerContent={headerTextWhenServicesSelected}
            state={IconStateType.SUCCESS_STATE}
          ></ConfirmationHeader>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.confirmationSubHeader,
              marginBottom: '1rem',
              marginTop: '2rem',
              scrollSnapMarginTop: '2rem',
            }}
          >
            {'Details'}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <PropertyServiceInfo
          infoContent={<SelectedServiceInfoSummary providerInfo={selectedProviders}></SelectedServiceInfoSummary>}
        ></PropertyServiceInfo>
      </Grid>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.confirmationSubHeader,
              marginTop: '1em',
            }}
          >
            {'Reminder'}
          </Typography>
        </Grid>
      </Grid>
      <PropertyServiceInfo
        infoContent={
          <Grid item xs={12}>
            <Typography variant="body1">{nextStepsText}</Typography>
          </Grid>
        }
      ></PropertyServiceInfo>
    </>
  );

  const contentWhenServicesNotSelected = (
    <>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader
            headerContent={headerTextWhenServicesNotSelected}
            state={IconStateType.WARNING_STATE}
          ></ConfirmationHeader>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.confirmationSubHeader,
              marginTop: '.5em',
            }}
          >
            {'Next Steps'}
          </Typography>
        </Grid>
      </Grid>
      <PropertyServiceInfo
        infoContent={
          <Grid item xs={12}>
            <Typography variant="body1">{nextStepsText}</Typography>
          </Grid>
        }
      ></PropertyServiceInfo>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.confirmationSubHeader,
              marginTop: '.5em',
            }}
          >
            {'Reminder'}
          </Typography>
        </Grid>
      </Grid>
      <PropertyServiceInfo
        infoContent={
          <Grid item xs={12}>
            <Typography variant="body1">{reminderText}</Typography>
          </Grid>
        }
      ></PropertyServiceInfo>
    </>
  );
  return (
    <>
      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      {isServicesSelected && contentWhenServicesSelected}
      {!isServicesSelected && contentWhenServicesNotSelected}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '0em',
        }}
      >
        <Grid item xs={12}>
          {!theme.custom.styles.showButtonWhenServicesSkipped && (
            <Button onClick={goToClaimsOverview} sx={{ ...theme.custom.styles.ctaCancelButton, marginTop: '3em' }}>
              {goToClaimsBtn}
            </Button>
          )}

          {theme.custom.styles.showButtonWhenServicesSkipped && (
            <Button
              onClick={goToClaimsOverview}
              sx={{
                ...theme.custom.styles.ctaNextButton,
                marginTop: '3em',
                marginLeft: '0px',
                '&.MuiButtonBase-root:hover': {
                  bgcolor: theme.custom.colors.primaryButtonHoverColor,
                },
              }}
            >
              {goToClaimsBtn}
            </Button>
          )}
        </Grid>
      </Grid>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
    </>
  );
}
